import React from "react";
import PublicLayout from "@/components/PublicLayout";
import Seo from "@/components/Seo";

const H2 = ({ children }) => <h2 className="font-heading text-2xl mt-10 mb-3 font-semibold text-slate-900">{children}</h2>;
const H3 = ({ children }) => <h3 className="font-heading text-lg mt-6 mb-2 font-semibold text-slate-900">{children}</h3>;
const P = ({ children }) => <p className="text-slate-600 leading-relaxed mb-3">{children}</p>;

export default function PrivacyPage() {
    return (
        <PublicLayout>
            <Seo
                title="Datenschutzerklärung | Bewerbung24.ai"
                description="Datenschutzerklärung von Bewerbung24.ai: Welche Daten wir verarbeiten, eingesetzte Dienstleister, Cookies & Einwilligung, Drittlandtransfer und deine Rechte."
                path="/datenschutz"
            />
            <section className="max-w-3xl mx-auto px-6 lg:px-8 py-20" data-testid="privacy-page">
                <h1 className="font-heading text-4xl font-bold text-slate-900 mb-2">Datenschutzerklärung</h1>
                <p className="text-slate-500">Stand: Juni 2026</p>

                <H2>1. Verantwortlicher</H2>
                <P>
                    Bewerbung24.ai, Inhaberin: Frau E. Decker, Am Kanal 22, 30599 Hannover, Deutschland ·
                    Zweigstelle: Im Mediapark 8, 50670 Köln.<br/>
                    E-Mail: <a href="mailto:info@bewerbung24.ai" className="text-red-600 hover:underline">info@bewerbung24.ai</a> ·
                    Soforthilfe: +49 170 5721522 · Web: www.bewerbung24.ai
                </P>

                <H2>2. Welche Daten wir verarbeiten</H2>
                <H3>2.1 Konto- und Stammdaten</H3>
                <P>Bei der Registrierung erheben wir Vorname, Nachname, E-Mail-Adresse und ein verschlüsselt gespeichertes Passwort. Optional kannst du Telefonnummer, Adresse und weitere Profilangaben hinterlegen.</P>
                <H3>2.2 Bewerbungsdaten</H3>
                <P>Zur Erstellung deiner Bewerbungsunterlagen verarbeiten wir die von dir eingegebenen oder hochgeladenen Daten: Lebenslauf-Angaben (Berufserfahrung, Ausbildung, Fähigkeiten, Sprachen, Zertifikate), hochgeladene Dokumente (Zeugnisse, Lebensläufe, Stellenanzeigen als PDF, DOCX oder Bild) sowie Bewerbungsfotos.</P>
                <H3>2.3 Zahlungsdaten</H3>
                <P>Zahlungen werden vollständig über unseren Zahlungsdienstleister Stripe abgewickelt. Wir selbst speichern keine Kreditkarten- oder Kontodaten, sondern nur Transaktionsreferenzen (Betrag, Produkt, Status).</P>
                <H3>2.4 Nutzungsdaten</H3>
                <P>Beim Besuch der Website verarbeiten wir technisch notwendige Daten (IP-Adresse, Browsertyp, Zeitpunkt des Zugriffs) zur Bereitstellung und Absicherung des Dienstes.</P>

                <H2>3. Zwecke und Rechtsgrundlagen</H2>
                <P>
                    Wir verarbeiten deine Daten zur Erbringung unseres Dienstes — der KI-gestützten Erstellung von Anschreiben, Lebensläufen und Bewerbungsmappen, dem Stellen-Matching sowie optionalen Funktionen wie Autopilot und Interview-Coach (Art. 6 Abs. 1 lit. b DSGVO, Vertragserfüllung).
                    Der Versand von Bewerbungen per E-Mail in deinem Namen (Autopilot) erfolgt nur nach deiner ausdrücklichen Aktivierung (Art. 6 Abs. 1 lit. a DSGVO, Einwilligung).
                    Transaktions-E-Mails (Registrierung, Kaufbestätigung, Passwort-Reset) versenden wir zur Vertragsabwicklung.
                </P>

                <H2>4. KI-Verarbeitung</H2>
                <P>
                    Zur Generierung von Texten, zur Dokumentenerkennung (OCR) und zur Foto-Optimierung setzen wir spezialisierte KI-Anbieter (u. a. OpenAI, Anthropic, Google) als Auftragsverarbeiter ein.
                    Dabei werden nur die für die jeweilige Funktion erforderlichen Inhalte übermittelt (z. B. dein Lebenslauf-Text und die Stellenbeschreibung). Deine Daten werden von diesen Anbietern nicht zum Training von KI-Modellen verwendet.
                </P>

                <H2>5. Empfänger und Auftragsverarbeiter</H2>
                <P>
                    Wir setzen folgende Dienstleister ein, mit denen Auftragsverarbeitungsverträge (Art. 28 DSGVO) bestehen:
                    Stripe (Zahlungsabwicklung), Resend und ein SMTP-Dienstleister (E-Mail-Versand), Cloud-Hosting- und Objektspeicher-Anbieter (Hosting der Plattform, Speicherung von Dokumenten und Fotos),
                    KI-Anbieter (siehe Ziffer 4) sowie — nur mit deiner Einwilligung — PostHog und Google (Webanalyse, siehe Ziffer 7).
                    Eine Weitergabe an sonstige Dritte erfolgt nicht, es sei denn, du beauftragst sie ausdrücklich (z. B. Versand einer Bewerbung an einen Arbeitgeber).
                </P>

                <H2>6. Speicherdauer</H2>
                <P>
                    Deine Daten bleiben gespeichert, solange dein Konto besteht. Du kannst dein Konto jederzeit im Dashboard unter „Datenschutz" vollständig löschen — dabei werden alle personenbezogenen Daten, Dokumente und Bewerbungen unwiderruflich entfernt.
                    Gesetzliche Aufbewahrungspflichten (z. B. für Rechnungsdaten, § 147 AO: 10 Jahre) bleiben unberührt.
                </P>

                <H2>7. Cookies, Einwilligung und Webanalyse</H2>
                <P>
                    Technisch notwendig: Zur Anmeldung nutzen wir ein Token im lokalen Speicher deines Browsers (localStorage), das du durch Abmelden jederzeit entfernen kannst.
                    Außerdem speichern wir lokal deine Sprachauswahl und deine Cookie-Einwilligungs-Entscheidung.
                </P>
                <P>
                    Analyse nur mit Einwilligung: Beim ersten Besuch fragen wir dich über einen Consent-Banner, ob du optionale Analyse-Dienste erlaubst.
                    Nur wenn du „Alle akzeptieren" wählst, aktivieren wir: (a) PostHog Inc. (USA) zur Webanalyse und Sitzungsaufzeichnung (Session-Recording),
                    um Fehler zu finden und die Bedienung zu verbessern — dabei wird ein Cookie (ph_…) gesetzt; (b) Google Tag Manager und Google Analytics 4
                    (Google Ireland Ltd. / Google LLC, USA) zur Reichweiten- und Kampagnenmessung im Google Consent Mode v2.
                    Rechtsgrundlage ist deine Einwilligung (Art. 6 Abs. 1 lit. a DSGVO, § 25 Abs. 1 TDDDG).
                    Du kannst deine Einwilligung jederzeit mit Wirkung für die Zukunft widerrufen, indem du die Website-Daten in deinem Browser löschst
                    (der Banner erscheint dann erneut) oder uns unter info@bewerbung24.ai kontaktierst.
                </P>

                <H2>8. Datenübermittlung in Drittländer (USA)</H2>
                <P>
                    Einige unserer Dienstleister haben ihren Sitz in den USA: OpenAI, Anthropic und Google (KI-Verarbeitung, siehe Ziffer 4),
                    Stripe (Zahlungsabwicklung) sowie — nur nach deiner Einwilligung — PostHog und Google Analytics.
                    Die Übermittlung erfolgt auf Grundlage des EU-U.S. Data Privacy Framework bzw. der EU-Standardvertragsklauseln (Art. 46 DSGVO).
                </P>

                <H2>9. Deine Rechte</H2>
                <P>
                    Du hast das Recht auf Auskunft (Art. 15), Berichtigung (Art. 16), Löschung (Art. 17), Einschränkung der Verarbeitung (Art. 18), Datenübertragbarkeit (Art. 20), Widerspruch (Art. 21) sowie Widerruf erteilter Einwilligungen.
                    Im Dashboard kannst du unter „Datenschutz" jederzeit selbstständig alle deine Daten als JSON exportieren (Art. 15 + 20) und dein Konto vollständig löschen (Art. 17).
                    Außerdem hast du das Recht, dich bei einer Datenschutz-Aufsichtsbehörde zu beschweren — zuständig ist die Landesbeauftragte für den Datenschutz Niedersachsen.
                </P>

                <H2>10. Datensicherheit</H2>
                <P>
                    Alle Verbindungen sind TLS-verschlüsselt. Passwörter werden ausschließlich als sichere Hashes gespeichert. Dokumente und Fotos liegen in zugriffsgeschützten Speichersystemen auf EU-Servern.
                </P>

                <H2>11. Änderungen</H2>
                <P>
                    Wir passen diese Datenschutzerklärung an, wenn sich unser Dienst oder die Rechtslage ändert. Die jeweils aktuelle Fassung findest du stets auf dieser Seite.
                </P>
            </section>
        </PublicLayout>
    );
}
